import type { RouteLocationNormalized } from 'vue-router'

/**
 * The page a user asked for before being sent through Keycloak, and the rules for
 * when it may be replayed.
 *
 * Kept out of `index.ts` so it carries no dependency on the router instance: the
 * guards that own the login round trip can be reasoned about — and tested — without
 * building the route table.
 */

export const POST_AUTH_REDIRECT_URL = 'post-auth-redirect'
/** Companion key holding when {@link POST_AUTH_REDIRECT_URL} was written. */
export const POST_AUTH_REDIRECT_SAVED_AT = 'post-auth-redirect-saved-at'

/**
 * How long a saved deep link stays restorable.
 *
 * The key is written on the way to Keycloak and consumed on the way back, so its
 * useful life is one login round trip. Abandon that round trip — close the tab at the
 * Keycloak page, never finish MFA — and it used to sit in localStorage FOREVER, so a
 * visit days later was silently redirected to a page saved back then. Bounding it
 * means a stale entry simply expires.
 *
 * An entry with no timestamp was written by a build from before this key existed, so
 * it counts as expired: that clears exactly the stale values causing those redirects
 * today, on the first load of a build that has this.
 */
export const POST_AUTH_REDIRECT_TTL_MS = 30 * 60 * 1000

export const setPostAuthRedirect = (path: string) => {
  localStorage.setItem(POST_AUTH_REDIRECT_URL, path)
  localStorage.setItem(POST_AUTH_REDIRECT_SAVED_AT, String(Date.now()))
}

const clearPostAuthRedirect = () => {
  localStorage.removeItem(POST_AUTH_REDIRECT_URL)
  localStorage.removeItem(POST_AUTH_REDIRECT_SAVED_AT)
}

export const getPostAuthRedirect = (shouldClear?: boolean) => {
  const url = localStorage.getItem(POST_AUTH_REDIRECT_URL)
  if (!url) return null

  const savedAt = Number(localStorage.getItem(POST_AUTH_REDIRECT_SAVED_AT))
  if (!savedAt || Date.now() - savedAt > POST_AUTH_REDIRECT_TTL_MS) {
    clearPostAuthRedirect()
    return null
  }

  if (shouldClear) clearPostAuthRedirect()
  return url
}

/**
 * Path Keycloak returns to after a login round trip — mirrors `keycloakRedirectUri`
 * in `main.ts`, read from the env here so a guard never pulls `@/main` into its
 * import cycle. Falls back to the app root, which is what the configured URL
 * resolves to in every deploy today.
 */
const postAuthLandingPath = (): string => {
  if (import.meta.env.DEV) return '/'

  const configured = import.meta.env.VITE_KEYCLOAK_LOGIN_URL
  if (!configured) return '/'
  try {
    return new URL(configured, window.location.origin).pathname || '/'
  } catch {
    return '/'
  }
}

/**
 * True only for the bare page Keycloak lands on after login — the one navigation a
 * saved deep link is allowed to replace.
 *
 * Without this check the restore fired on ANY navigation, so opening a shared link
 * directly (`/public/gpt/:token`) bounced the visitor to a leftover saved route
 * instead, and for a visitor without EverGPT access that ends on /not-authorised. A
 * landing page carrying its own query — notably `/?gptUuid=…` from "Open this GPT" —
 * is a real destination too, so it is excluded as well.
 */
export const isPostAuthLandingRoute = (to: RouteLocationNormalized): boolean =>
  to.path === postAuthLandingPath() &&
  !to.hash &&
  Object.keys(to.query).length === 0
